import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import { GatewaysListComponent } from "./gateways-list-component";
import styles from "./gateways-list-page.modules.scss";
import { useGatewaysData } from "./hooks/use-gateways-data";
import { loadGatewaysMachinesPageData } from "@src/cloud-connect/gateways-with-machines/use-cases/load-gateways-machines-data";
import { setGatewaysWithMachinesResultsReady } from "@src/reducers/gateways-machines-slice";

const cssClassName = "gateways-list-page";

export const GatewaysAndSourcesListPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [refetchedGatewayUuid, setRefetchedGatewayUuid] = useState("");
  const targetGatewayHighlightRef = useRef<{
    gatewayUuid: string;
    discoveryDeadline: number;
    found: boolean;
    expired: boolean;
    timeout: number;
    clearFromUrl: () => void;
  } | null>(null);

  const gatewaysData = useGatewaysData(true);
  const targetGatewayUuid =
    new URLSearchParams(location.search).get("gateway_uuid")?.trim() || "";
  const isGatewayRefetched = refetchedGatewayUuid === targetGatewayUuid;

  useEffect(() => {
    return () => {
      dispatch(setGatewaysWithMachinesResultsReady(false));
    };
  }, [dispatch]);

  useEffect(() => {
    let isCancelled = false;

    setRefetchedGatewayUuid("");

    if (!targetGatewayUuid) {
      return;
    }

    const refetchGateways = async () => {
      await loadGatewaysMachinesPageData();

      if (!isCancelled) {
        setRefetchedGatewayUuid(targetGatewayUuid);
      }
    };

    refetchGateways();

    return () => {
      isCancelled = true;
    };
  }, [targetGatewayUuid]);

  useEffect(() => {
    if (!targetGatewayUuid) return;

    const clearFromUrl = () => {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get("gateway_uuid")?.trim() !== targetGatewayUuid) {
        return;
      }

      searchParams.delete("gateway_uuid");
      const nextSearch = searchParams.toString();
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
          hash: window.location.hash,
        },
        { replace: true },
      );
    };

    const highlightState = {
      gatewayUuid: targetGatewayUuid,
      discoveryDeadline: Date.now() + 30000,
      found: false,
      expired: false,
      timeout: 0,
      clearFromUrl,
    };
    highlightState.timeout = window.setTimeout(() => {
      if (!highlightState.found) {
        highlightState.expired = true;
        clearFromUrl();
      }
    }, 30000);
    targetGatewayHighlightRef.current = highlightState;

    return () => {
      window.clearTimeout(highlightState.timeout);
      if (targetGatewayHighlightRef.current === highlightState) {
        targetGatewayHighlightRef.current = null;
      }
    };
  }, [targetGatewayUuid, navigate]);

  useEffect(() => {
    if (
      !targetGatewayUuid ||
      !isGatewayRefetched ||
      !gatewaysData.gatewaysListsResultsReady
    ) {
      return;
    }

    const highlightState = targetGatewayHighlightRef.current;
    if (
      !highlightState ||
      highlightState.gatewayUuid !== targetGatewayUuid ||
      highlightState.found
    ) {
      return;
    }

    let cancelled = false;
    let retryTimeout: number | undefined;

    const tryScrollToGateway = () => {
      const currentHighlightState = targetGatewayHighlightRef.current;
      if (
        cancelled ||
        currentHighlightState?.gatewayUuid !== targetGatewayUuid ||
        currentHighlightState.found ||
        currentHighlightState.expired
      ) {
        return;
      }

      if (Date.now() >= currentHighlightState.discoveryDeadline) {
        currentHighlightState.expired = true;
        currentHighlightState.clearFromUrl();
        return;
      }

      const targetGatewayRow = document.getElementById(
        `gateway-row-${targetGatewayUuid}`,
      ) as HTMLTableRowElement | null;

      if (!targetGatewayRow) {
        retryTimeout = window.setTimeout(tryScrollToGateway, 100);
        return;
      }

      if (
        currentHighlightState.gatewayUuid !== targetGatewayUuid ||
        currentHighlightState.found ||
        currentHighlightState.expired ||
        Date.now() >= currentHighlightState.discoveryDeadline
      ) {
        return;
      }

      currentHighlightState.found = true;
      window.clearTimeout(currentHighlightState.timeout);
      currentHighlightState.timeout = window.setTimeout(
        currentHighlightState.clearFromUrl,
        30000,
      );

      targetGatewayRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    };

    tryScrollToGateway();

    return () => {
      cancelled = true;
      if (retryTimeout !== undefined) window.clearTimeout(retryTimeout);
    };
  }, [
    targetGatewayUuid,
    isGatewayRefetched,
    gatewaysData.gatewaysListsResultsReady,
  ]);

  return (
    <MainContainer>
      <div className={styles[cssClassName]}>
        <RoundBorderComponentContainer>
          {gatewaysData.viewState === "Error" ? (
            <p>{gatewaysData.error || "Failed to load gateways."}</p>
          ) : (
            <GatewaysListComponent viewToBeRendered={gatewaysData.viewState} />
          )}
        </RoundBorderComponentContainer>
      </div>
    </MainContainer>
  );
};
